/**
 * Phase 8 — Photos picker. Stella emits `[[PHOTOS:anchor_id=…]]` after
 * recording a fresh anchor on a multi-photo estimate; the customer
 * taps cards to attach the anchor to the right photo(s).
 *
 * Multi-select unlike `OptionsCardGrid`. Apply round-trips a synthetic
 * `[[SELECTED:photos=<id>,<id>;anchor_id=<id>]]` chat turn.
 */

import { useState } from "react";

export interface PhotosPickerCard {
  photo_id: string;
  thumbnail_url: string | null;
  original_url: string | null;
  room_label: string | null;
  created_at: string;
}

export interface PhotosPickerPayload {
  /** "anchor" (Phase 8, link a measurement) or "render" (Phase 21, pick a
   * photo to remodel-render). Absent ⇒ "anchor" for back-compat. */
  purpose?: "anchor" | "render";
  photos: PhotosPickerCard[];
  // Anchor-flow fields (present when purpose !== "render").
  anchor_id?: string;
  anchor_value_text?: string;
  anchor_value_inches?: number | null;
  // Render-flow field (present when purpose === "render").
  render_prompt?: string;
}

interface Props {
  payload: PhotosPickerPayload;
  /** Set when the customer has already applied (collapsed view on reload). */
  appliedPhotoIds?: string[];
  onApply: (photoIds: string[]) => void;
  disabled?: boolean;
}

export function PhotosPicker({
  payload,
  appliedPhotoIds,
  onApply,
  disabled = false,
}: Props) {
  const isRender = payload.purpose === "render";
  const collapsed = appliedPhotoIds && appliedPhotoIds.length > 0;
  const [selected, setSelected] = useState<Set<string>>(new Set());

  if (collapsed) {
    if (isRender) {
      return (
        <section
          aria-label="Rendering"
          className="rounded-xl border border-starz-gold/15 bg-starz-cream/20 p-3 text-xs italic text-warm-gray"
        >
          Rendering your selected photo…
        </section>
      );
    }
    const n = appliedPhotoIds!.length;
    const word = n === 1 ? "photo" : "photos";
    return (
      <section
        aria-label="Measurement linked"
        className="rounded-xl border border-starz-gold/15 bg-starz-cream/20 p-3 text-xs italic text-warm-gray"
      >
        Linked the measurement "{payload.anchor_value_text}" to {n} of your {word}.
      </section>
    );
  }

  function toggle(id: string) {
    setSelected((prev) => {
      // Render picks exactly one photo; anchor links can pick several.
      if (isRender) return prev.has(id) ? new Set() : new Set([id]);
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function apply() {
    if (selected.size === 0) return;
    onApply(Array.from(selected));
  }

  return (
    <section
      aria-label={isRender ? "Pick which photo to render" : "Pick which photo this measurement applies to"}
      className="space-y-3 rounded-xl border border-starz-gold/20 bg-starz-cream/30 p-4"
    >
      <header>
        <p className="text-xs font-semibold uppercase tracking-widest text-starz-gold">
          {isRender ? "Choose a photo to render" : "Apply this measurement"}
        </p>
        <p className="mt-1 text-sm text-starz-charcoal">
          {isRender ? (
            "Which photo should I render your change on?"
          ) : (
            <>
              You said: <span className="italic">"{payload.anchor_value_text}"</span>. Which
              photo does this go with? You can pick more than one.
            </>
          )}
        </p>
      </header>
      <ul className="grid grid-cols-2 gap-3 md:grid-cols-3">
        {payload.photos.map((c) => {
          const isSelected = selected.has(c.photo_id);
          const src = c.thumbnail_url ?? c.original_url;
          return (
            <li key={c.photo_id}>
              <button
                type="button"
                onClick={() => toggle(c.photo_id)}
                disabled={disabled}
                aria-pressed={isSelected}
                className={
                  "flex w-full flex-col gap-2 rounded-xl border-2 bg-white p-2 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60 " +
                  (isSelected
                    ? "border-starz-gold ring-2 ring-starz-gold/40"
                    : "border-starz-gold/15 hover:border-starz-gold/40")
                }
              >
                {src ? (
                  <img
                    src={src}
                    alt={c.room_label ?? "Photo"}
                    loading="lazy"
                    className="aspect-[4/3] w-full rounded-md object-cover"
                  />
                ) : (
                  <div className="grid aspect-[4/3] w-full place-items-center rounded-md bg-starz-cream text-xs text-warm-gray">
                    Uploading…
                  </div>
                )}
                <div className="text-xs text-warm-gray">
                  {c.room_label ?? new Date(c.created_at).toLocaleDateString()}
                </div>
                {isSelected && (
                  <span className="text-xs font-semibold uppercase tracking-widest text-starz-gold">
                    Selected
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
      <div className="flex items-center justify-end gap-3">
        <span className="text-xs text-warm-gray">
          {selected.size} selected
        </span>
        <button
          type="button"
          onClick={apply}
          disabled={disabled || selected.size === 0}
          className="min-h-[44px] rounded-lg bg-starz-navy px-5 py-2 text-sm font-semibold uppercase tracking-wide text-starz-off-white hover:bg-starz-navy/90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isRender ? "Render this photo" : "Apply to selected"}
        </button>
      </div>
    </section>
  );
}
